import { buildPrimaryFetchInit, resolvePrimaryEnvironmentHttpUrl } from "./environments/primary";

export interface GitHubAgentCapability {
  readonly connected: boolean;
  readonly login: string | null;
  readonly repoAccessUsable: boolean;
  readonly repoCount: number | null;
  readonly warnings: readonly string[];
  readonly error: string | null;
  readonly requiresCmmdAuth: boolean;
}

interface FetchGitHubAgentCapabilityOptions {
  readonly includeRepositories?: boolean;
}

export async function fetchGitHubAuthorizeUrl(): Promise<string> {
  // Pass our own origin so CMMD's OAuth callback returns the user to Forge
  // (allow-listed server-side) instead of landing them on the CMMD site.
  const returnTo = typeof window !== "undefined" ? window.location.origin : null;
  const response = await fetch(
    resolvePrimaryEnvironmentHttpUrl(
      "/.well-known/forge/github/authorize-url",
      returnTo ? { return_to: returnTo } : undefined,
    ),
    buildPrimaryFetchInit({ credentials: "include" }),
  );
  const body = (await response.json().catch(() => ({}))) as {
    readonly url?: unknown;
    readonly error?: unknown;
  };
  if (!response.ok || typeof body.url !== "string") {
    const message =
      typeof body.error === "string"
        ? body.error
        : `GitHub authorization failed (${response.status}).`;
    throw new Error(message);
  }
  return body.url;
}

async function fetchGitHubAgentCapabilityInternal(
  options: FetchGitHubAgentCapabilityOptions = {},
): Promise<GitHubAgentCapability> {
  const statusResponse = await fetch(
    resolvePrimaryEnvironmentHttpUrl("/.well-known/forge/github/status"),
    buildPrimaryFetchInit({ credentials: "include" }),
  );
  const statusBody = (await statusResponse.json().catch(() => ({}))) as {
    connected?: unknown;
    github_login?: unknown;
    user_repo_access_usable?: unknown;
    code?: unknown;
    error?: unknown;
  };
  if (statusResponse.status === 401 || statusBody.code === "CMMD_REAUTH_REQUIRED") {
    return {
      connected: false,
      login: null,
      repoAccessUsable: false,
      repoCount: null,
      warnings: [],
      error:
        typeof statusBody.error === "string"
          ? statusBody.error
          : "Sign back in to CMMD to check GitHub access.",
      requiresCmmdAuth: true,
    };
  }
  const connected = statusBody.connected === true;
  const login = typeof statusBody.github_login === "string" ? statusBody.github_login : null;
  const repoAccessUsable = statusBody.user_repo_access_usable === true;
  const error = statusResponse.ok
    ? null
    : typeof statusBody.error === "string"
      ? statusBody.error
      : `GitHub access check failed (${statusResponse.status}).`;

  if (!statusResponse.ok || !connected || !repoAccessUsable || !options.includeRepositories) {
    return {
      connected,
      login,
      repoAccessUsable,
      repoCount: null,
      warnings: [],
      error,
      requiresCmmdAuth: false,
    };
  }

  const reposResponse = await fetch(
    resolvePrimaryEnvironmentHttpUrl("/.well-known/forge/github/repos"),
    buildPrimaryFetchInit({ credentials: "include" }),
  );
  const reposBody = (await reposResponse.json().catch(() => ({}))) as {
    repos?: unknown;
    warnings?: unknown;
    error?: unknown;
  };
  const warnings = Array.isArray(reposBody.warnings)
    ? reposBody.warnings.filter((warning): warning is string => typeof warning === "string")
    : [];

  return {
    connected,
    login,
    repoAccessUsable,
    repoCount: Array.isArray(reposBody.repos) ? reposBody.repos.length : null,
    warnings,
    error: reposResponse.ok
      ? null
      : typeof reposBody.error === "string"
        ? reposBody.error
        : `GitHub repository visibility check failed (${reposResponse.status}).`,
    requiresCmmdAuth: false,
  };
}

export async function fetchGitHubAgentCapability(): Promise<GitHubAgentCapability> {
  return fetchGitHubAgentCapabilityInternal();
}

export async function fetchGitHubAgentCapabilityWithRepositories(): Promise<GitHubAgentCapability> {
  return fetchGitHubAgentCapabilityInternal({ includeRepositories: true });
}
